import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2, Send, Check, LifeBuoy, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function DepositRecoveryPanel() {
  const [sending, setSending] = useState("");
  const [sent, setSent] = useState({});
  const { data, isLoading, refetch } = useQuery({ queryKey: ["deposit-recovery"], queryFn: () => api.get("/admin/deposit-recovery").then((r) => r.data) });
  const rows = data?.appointments || [];

  const sendRequest = async (id) => {
    setSending(id);
    try {
      const { data } = await api.post(`/admin/appointments/${id}/payment-request`);
      if (data.sent) { toast.success(`Payment reminder sent to ${data.to}`); setSent((s) => ({ ...s, [id]: true })); }
      else toast.error("Email could not be sent (check email settings)");
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } finally { setSending(""); }
  };

  return (
    <div className="eyw-card p-5 mt-6" data-testid="deposit-recovery-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><AlertCircle size={18} className="text-[#B5794E]" /> Deposit recovery</h3>
        {data && <span className="text-sm text-[#4A5D6B]">{money(data.total_outstanding)} outstanding · <b className="text-[#B45151]">{data.overdue_count} overdue</b></span>}
      </div>
      <p className="text-xs text-[#8FA89B] mb-3">Appointments where a deposit was paid but a balance is still owed. Collect the balance at the front desk, or send the client a reminder.</p>

      {isLoading ? <div className="text-[#8FA89B] text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div>
        : rows.length === 0 ? <p className="text-sm text-[#8FA89B] py-4" data-testid="deposit-recovery-empty">No outstanding deposit balances — you're all caught up. 🎉</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#8FA89B] border-b border-[#E5E0D8]">
              <th className="py-2 pr-3">Date</th><th className="py-2 px-2">Service</th><th className="py-2 px-2">Client</th><th className="py-2 px-2 text-right">Paid</th><th className="py-2 px-2 text-right">Owed</th><th className="py-2 px-2 text-right"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`border-b border-[#F1EEE8] ${r.overdue ? "bg-[#B45151]/[0.04]" : ""}`} data-testid={`deposit-row-${r.id}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(r.date)}{r.overdue && <span className="ml-1.5 text-[10px] bg-[#B45151]/12 text-[#B45151] px-1.5 py-0.5 rounded-full">overdue</span>}</td>
                  <td className="py-2 px-2 text-[#1A242B]">{r.service_title}</td>
                  <td className="py-2 px-2 text-[#4A5D6B]">{r.client}</td>
                  <td className="py-2 px-2 text-right text-[#8FA89B]">{money(r.amount_paid)}</td>
                  <td className="py-2 px-2 text-right font-semibold text-[#B5794E]">{money(r.balance_due)}</td>
                  <td className="py-2 px-2 text-right">
                    <button onClick={() => sendRequest(r.id)} disabled={sending === r.id} data-testid={`deposit-remind-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-[#144F75]/10 text-[#144F75] hover:bg-[#144F75] hover:text-white transition-colors disabled:opacity-50">
                      {sending === r.id ? <Loader2 size={11} className="animate-spin" /> : (sent[r.id] || r.last_payment_request_at) ? <Check size={11} /> : <Send size={11} />}
                      {(sent[r.id] || r.last_payment_request_at) ? "Reminded" : "Send reminder"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <StripeSessionRecovery />
    </div>
  );
}

function StripeSessionRecovery() {
  const [sid, setSid] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reconcile = async () => {
    const s = sid.trim();
    if (!s) return toast.error("Paste a Stripe checkout session id (cs_live_… / cs_test_…)");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/reconcile-stripe-session", { session_id: s });
      setResult(data);
      if (data.ok) toast.success(data.already_paid_before ? "Session was already reconciled" : "Session reconciled");
      else toast.error(`Not reconciled: ${data.reason || "unpaid"}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      setResult({ error: typeof d === "string" ? d : (d?.message || "That request didn't go through. Please try again.") });
      toast.error(typeof d === "string" ? d : "Could not reconcile session");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-[#E5E0D8]" data-testid="stripe-recovery">
      <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-1"><LifeBuoy size={18} className="text-[#144F75]" /> Stripe session recovery</h3>
      <p className="text-xs text-[#8FA89B] mb-3">Reconcile a paid Stripe Checkout session that never created its record (e.g. the buyer closed the tab before the webhook was live). Paste the full session id — it fetches the paid session from Stripe and creates the missing transaction, confirmed booking/registration and revenue split. Safe to re-run (idempotent).</p>
      <div className="flex flex-wrap gap-2">
        <input value={sid} onChange={(e) => setSid(e.target.value)} placeholder="cs_live_…" data-testid="stripe-recovery-input"
          className="eyw-input flex-1 min-w-[240px] font-mono text-sm" />
        <button onClick={reconcile} disabled={busy} data-testid="stripe-recovery-run"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#144F75] text-white text-sm hover:bg-[#0f3d5c] transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Reconcile
        </button>
      </div>
      {result && (
        <div className={`mt-3 rounded-lg border p-3 text-xs ${result.error || result.ok === false ? "border-[#B45151]/30 bg-[#B45151]/[0.05] text-[#B45151]" : "border-[#5C8A72]/30 bg-[#5C8A72]/[0.06] text-[#3f6b53]"}`} data-testid="stripe-recovery-result">
          {result.error ? <p className="font-medium">{result.error}</p> : result.ok === false ? (
            <p>Session not reconciled — <b>{result.reason}</b> (Stripe payment_status: {result.payment_status || "?"}).</p>
          ) : (
            <div className="space-y-0.5 text-[#3f6b53]">
              <p className="font-medium text-[#1A242B]">{result.already_paid_before ? "Already reconciled ✓" : "Reconciled ✓"}</p>
              <p>Kind: <b>{result.kind}</b> · Amount: <b>${Number(result.amount || 0).toFixed(2)}</b> · Ref: <span className="font-mono">{result.ref_id}</span></p>
              <p>Transaction: {result.tx_payment_status}{result.created_missing_tx ? " (created)" : ""} · Record found: {String(result.ref_found)} {result.ref_status ? `(${result.ref_status})` : ""} · Revenue rows: {result.revenue_records}</p>
              {result.note && <p className="text-[#B5794E] mt-1">{result.note}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
